import React, { useMemo } from 'react';
import ChartCanvas from '../ChartCanvas';
import PlatformChips from '../PlatformChips';
import {
  platformLabel, fmtNum, fmtShortDate, stripHtml, tagLabel, PALETTE,
} from '../soundstatsHelpers';
import { horizBarPaletteConfig } from '../soundstatsCharts';

// Audience & profile insights report.

function Kpi({ value, label }) {
  return <div className="ss-kpi"><div className="ss-kpi-v">{value}</div><div className="ss-kpi-l">{label}</div></div>;
}

function collectPosts(platforms) {
  const posts = [];
  platforms.forEach(p => {
    const top = (p.object && p.object.top) || {};
    const arr = (top.recentPosts && top.recentPosts.length) ? top.recentPosts : top.posts || [];
    arr.forEach(po => posts.push(po));
  });
  posts.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  return posts;
}

function PostTable({ posts, title }) {
  const rows = posts.slice(0, 20).map((po, i) => {
    const txt = stripHtml(po.text).slice(0, 120);
    return (
      <tr key={i}>
        <td className="ss-td-title">{txt || '—'}</td>
        <td className="ss-td-dim">{fmtShortDate(po.date)}</td>
        <td className="ss-td-dim">{fmtNum(po.viewCount)}</td>
        <td className="ss-td-dim">{fmtNum(po.likeCount)}</td>
        <td className="ss-td-dim">{fmtNum(po.commentCount)}</td>
      </tr>
    );
  });
  return (
    <div className="ss-card">
      <div className="ss-card-head">{title}</div>
      <div className="ss-table-wrap">
        <table className="ss-table">
          <thead><tr><th>Post</th><th>Date</th><th>Views</th><th>Likes</th><th>Comments</th></tr></thead>
          <tbody>{rows}</tbody>
        </table>
      </div>
    </div>
  );
}

function CombinedReport({ platforms }) {
  const statDefs = [
    ['followerCount', 'Followers'], ['postCount', 'Posts'], ['viewCount', 'Views'],
    ['engagementCount', 'Engagement'], ['totalLikes', 'Total likes'],
  ];
  const agg = {};
  let engSum = 0;
  let engN = 0;
  platforms.forEach(p => {
    const stats = (p.object && p.object.audience && p.object.audience.stats) || {};
    statDefs.forEach(s => {
      const v = stats[s[0]];
      if (typeof v === 'number') agg[s[0]] = (agg[s[0]] || 0) + v;
    });
    if (typeof stats.engagementRate === 'number') { engSum += stats.engagementRate; engN++; }
  });

  const affMap = {};
  platforms.forEach(p => {
    const aud = (p.object && p.object.audience) || {};
    ['audienceLike', 'audienceFollower', 'audienceCommenter'].forEach(k => {
      const arr = (aud[k] && aud[k].brandAffinities) || [];
      arr.forEach(it => {
        if (!it || typeof it !== 'object' || !it.name) return;
        const v = it.affinity != null ? it.affinity : 1;
        affMap[it.name] = (affMap[it.name] || 0) + v;
      });
    });
  });
  const affinities = Object.keys(affMap).map(name => ({ name, affinity: affMap[name] })).sort((a, b) => b.affinity - a.affinity);

  const interestSet = {};
  const interests = [];
  platforms.forEach(p => {
    const aud = (p.object && p.object.audience) || {};
    (aud.interests || []).forEach(i => {
      const label = tagLabel(i);
      if (label && !interestSet[label]) { interestSet[label] = true; interests.push(label); }
    });
  });

  const posts = useMemo(() => collectPosts(platforms), [platforms]);
  const affCfg = useMemo(() => {
    if (!affinities.length) return null;
    const top = affinities.slice(0, 12);
    return horizBarPaletteConfig(top.map(a => a.name), top.map(a => a.affinity), PALETTE);
  }, [affinities]);

  return (
    <>
      <div className="ss-card">
        <div className="ss-card-head">Combined audience stats</div>
        <div className="ss-gkpi-grid">
          {statDefs.map(s => agg[s[0]] != null && <Kpi key={s[0]} value={fmtNum(agg[s[0]])} label={`${s[1]} (total)`} />)}
          <Kpi value={fmtNum(platforms.length)} label="Platforms tracked" />
          {engN > 0 && <Kpi value={`${(engSum / engN * 100).toFixed(2)}%`} label="Engagement rate (avg)" />}
        </div>
      </div>
      {affCfg && (
        <div className="ss-card">
          <div className="ss-card-head">Brand affinities · all platforms</div>
          <ChartCanvas config={affCfg} height={Math.max(220, Math.min(affinities.length, 12) * 34)} />
        </div>
      )}
      {interests.length > 0 && (
        <div className="ss-card">
          <div className="ss-card-head">Interests</div>
          <div className="ss-rp-chips">{interests.map((i, idx) => <span key={idx} className="ss-chip">{i}</span>)}</div>
        </div>
      )}
      {posts.length > 0 && <PostTable posts={posts} title="Recent posts · all platforms" />}
    </>
  );
}

function SingleReport({ entry }) {
  const obj = entry.object || {};
  const prof = obj.userProfile || {};
  const aud = obj.audience || {};
  const stats = aud.stats || {};
  const top = obj.top || {};
  const desc = stripHtml(prof.description);

  const pf = [
    ['Account', prof.accountType], ['Category', prof.accountCategory],
    ['Gender', prof.gender], ['Age group', prof.ageGroup],
    ['Language', prof.language],
    ['Verified', prof.verified ? 'Yes' : ''], ['Official artist', prof.officialArtist ? 'Yes' : ''],
  ].filter(f => f[1] != null && f[1] !== '');

  const statDefs = [
    ['followerCount', 'Followers'], ['postCount', 'Posts'], ['viewCount', 'Views'],
    ['engagementCount', 'Engagement'], ['averageLikesPerPost', 'Avg likes/post'],
    ['averageCommentsPerPost', 'Avg comments/post'], ['totalLikes', 'Total likes'],
    ['engagementRate', 'Engagement rate'],
  ];

  const affinities = [];
  ['audienceLike', 'audienceFollower', 'audienceCommenter'].forEach(k => {
    const arr = (aud[k] && aud[k].brandAffinities) || [];
    arr.forEach(it => { if (it && typeof it === 'object' && it.name) affinities.push(it); });
  });
  (aud.brandsAffinity || []).forEach(name => { if (affinities.length < 20) affinities.push({ name, affinity: null }); });
  affinities.sort((a, b) => (b.affinity || 0) - (a.affinity || 0));

  const interests = aud.interests || [];
  const tags = (aud.relevantTags || []).concat(aud.similarUsers || []);
  const posts = (top.recentPosts && top.recentPosts.length) ? top.recentPosts : top.posts || [];

  const affCfg = useMemo(() => {
    if (!affinities.length) return null;
    const top = affinities.slice(0, 12);
    return horizBarPaletteConfig(top.map(a => a.name), top.map(a => a.affinity != null ? a.affinity : 1), PALETTE);
  }, [affinities]);

  return (
    <>
      <div className="ss-grid ss-g-2">
        <div className="ss-card">
          <div className="ss-card-head">{platformLabel(entry.key)} · profile</div>
          <div className="ss-rp-name">{prof.fullName || platformLabel(entry.key)}</div>
          {desc && <p className="ss-rp-desc">{desc}</p>}
          <div className="ss-kv">{pf.map((f, i) => <div key={i} className="ss-kv-row"><span className="ss-kv-k">{f[0]}</span><span className="ss-kv-v">{f[1]}</span></div>)}</div>
        </div>
        <div className="ss-card">
          <div className="ss-card-head">Audience stats</div>
          <div className="ss-gkpi-grid">
            {statDefs.map(s => {
              const v = stats[s[0]];
              if (v == null || v === '') return null;
              const display = s[0] === 'engagementRate' ? `${(Number(v) * 100).toFixed(2)}%` : fmtNum(v);
              return <Kpi key={s[0]} value={display} label={s[1]} />;
            })}
          </div>
        </div>
      </div>
      {affCfg && (
        <div className="ss-card">
          <div className="ss-card-head">Brand affinities</div>
          <ChartCanvas config={affCfg} height={Math.max(220, Math.min(affinities.length, 12) * 34)} />
        </div>
      )}
      {(interests.length > 0 || tags.length > 0) && (
        <div className="ss-card">
          <div className="ss-card-head">Interests & tags</div>
          {interests.length > 0 && <div className="ss-rp-section"><div className="ss-rp-sub">Interests</div><div className="ss-rp-chips">{interests.map((i, idx) => <span key={idx} className="ss-chip">{tagLabel(i)}</span>)}</div></div>}
          {tags.length > 0 && <div className="ss-rp-section"><div className="ss-rp-sub">Relevant tags</div><div className="ss-rp-chips">{tags.map((i, idx) => <span key={idx} className="ss-chip">{tagLabel(i)}</span>)}</div></div>}
        </div>
      )}
      {posts.length > 0 && <PostTable posts={posts} title="Recent posts" />}
    </>
  );
}

export default function ReportGroup({ platforms, entries, chipIdx, setChipIdx }) {
  return (
    <>
      <div className="ss-content-title"><h2>Report</h2><span className="ss-sec-hint">Audience & profile insights</span></div>
      <PlatformChips entries={entries} idx={chipIdx} onPick={setChipIdx} />
      {chipIdx === 0 ? <CombinedReport platforms={platforms} /> : entries[chipIdx] ? <SingleReport entry={entries[chipIdx]} /> : null}
    </>
  );
}
